import React, { useEffect, useState } from 'react';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import TextField from '@mui/material/TextField';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import CircularProgress from '@mui/material/CircularProgress';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:3001';
const IT_DAYS = ['Domenica', 'Lunedì', 'Martedì', 'Mercoledì', 'Giovedì', 'Venerdì', 'Sabato'];

function toYmd(date) {
  const pad = (n) => String(n).padStart(2, '0');
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;
}

export default function AppointmentForm({ onSubmit, calendarEnabled, availableDays = [] }) {
  const [fullName, setFullName] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [contactReason, setContactReason] = useState('');
  const [selectedDate, setSelectedDate] = useState(null);
  const [slots, setSlots] = useState([]);
  const [selectedSlot, setSelectedSlot] = useState('');
  const [slotsLoading, setSlotsLoading] = useState(false);
  const [slotsError, setSlotsError] = useState('');

  const allowedDays = availableDays.length ? availableDays : IT_DAYS.slice(1, 6);

  useEffect(() => {
    if (!calendarEnabled || !selectedDate) {
      setSlots([]);
      setSelectedSlot('');
      return undefined;
    }
    let cancelled = false;
    const load = async () => {
      setSlotsLoading(true);
      setSlotsError('');
      setSelectedSlot('');
      try {
        const response = await fetch(`${BACKEND_URL}/get-available-time-slots`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ date: toYmd(selectedDate) }),
        });
        const data = await response.json();
        if (!response.ok) throw new Error(data.error || 'Impossibile caricare gli orari');
        if (!cancelled) setSlots(data.availableTimeSlots || []);
      } catch (error) {
        if (!cancelled) {
          setSlots([]);
          setSlotsError(error.message);
        }
      } finally {
        if (!cancelled) setSlotsLoading(false);
      }
    };
    load();
    return () => {
      cancelled = true;
    };
  }, [calendarEnabled, selectedDate]);

  const handleSubmit = (event) => {
    event.preventDefault();
    if (calendarEnabled && (!selectedDate || !selectedSlot)) return;
    const appointmentDateTime = calendarEnabled
      ? `${toYmd(selectedDate)}T${selectedSlot}:00`
      : undefined;
    onSubmit({
      fullName,
      email,
      phone,
      contactReason,
      appointmentDateTime,
    });
  };

  return (
    <Box component="form" onSubmit={handleSubmit} sx={{ p: 2 }}>
      <Typography variant="h6">
        {calendarEnabled ? 'Prenota un appuntamento' : 'Inserisci i tuoi dati'}
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        {calendarEnabled
          ? 'Scegli un giorno e un orario libero, poi conferma i tuoi recapiti.'
          : 'Ti ricontatteremo il prima possibile.'}
      </Typography>
      {calendarEnabled && (
        <>
          <DatePicker
            label="Data"
            value={selectedDate}
            onChange={(value) => setSelectedDate(value)}
            minDate={new Date()}
            shouldDisableDate={(date) => !allowedDays.includes(IT_DAYS[date.getDay()])}
            renderInput={(params) => <TextField {...params} fullWidth required sx={{ mb: 2 }} />}
          />
          {slotsLoading && (
            <Box sx={{ display: 'flex', justifyContent: 'center', my: 1 }}>
              <CircularProgress size={22} />
            </Box>
          )}
          {slotsError && (
            <Typography variant="caption" color="error" sx={{ display: 'block', mb: 1 }}>
              {slotsError}
            </Typography>
          )}
          {!slotsLoading && selectedDate && slots.length === 0 && !slotsError && (
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
              Nessun orario disponibile in questa data.
            </Typography>
          )}
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 2 }}>
            {slots.map((slot) => (
              <Chip
                key={slot}
                label={slot}
                clickable
                color={selectedSlot === slot ? 'primary' : 'default'}
                variant={selectedSlot === slot ? 'filled' : 'outlined'}
                onClick={() => setSelectedSlot(slot)}
              />
            ))}
          </Box>
        </>
      )}
      <TextField
        label="Nome e Cognome"
        value={fullName}
        onChange={(e) => setFullName(e.target.value)}
        required
        fullWidth
        sx={{ mb: 2 }}
      />
      <TextField
        label="Email"
        type="email"
        value={email}
        onChange={(e) => setEmail(e.target.value)}
        required
        fullWidth
        sx={{ mb: 2 }}
      />
      <TextField
        label="Telefono"
        value={phone}
        onChange={(e) => setPhone(e.target.value)}
        required
        fullWidth
        sx={{ mb: 2 }}
      />
      <TextField
        label="Motivo del Contatto"
        value={contactReason}
        onChange={(e) => setContactReason(e.target.value)}
        required
        fullWidth
        multiline
        rows={3}
        sx={{ mb: 2 }}
      />
      <Button
        type="submit"
        variant="contained"
        fullWidth
        disabled={calendarEnabled && (!selectedDate || !selectedSlot)}
      >
        {calendarEnabled ? 'Conferma appuntamento' : 'Invia'}
      </Button>
    </Box>
  );
}
